import Vue, { VNode } from 'vue';

type Options = {
  handler: (e: Event) => void;
  include?: () => (HTMLElement | null | undefined)[];
  closeConditional?: (e: Event) => boolean;
  isActive?: boolean;
  bubble?: boolean;
  vm?: any;
};

type Ctx = {
  el: HTMLElement;
  options: Required<Options>;
  _listening: boolean;
  _lastMousedown: Event | null;
  _onDown: (e: Event) => void;
  _onUp: (e: Event) => void;
};

const defaults: Required<Omit<Options, 'handler'>> = {
  include: () => [],
  closeConditional: () => true,
  isActive: true,
  bubble: false,
  vm: undefined as any,
};

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function getPath(e: any): any[] {
  if (e.composedPath) return e.composedPath();
  if (e.path) return e.path;
  const path: any[] = [];
  let node = e.target as Node | null;
  while (node) {
    path.push(node);
    node = (node as any).parentNode || null;
  }
  path.push(window, document);
  return path;
}

function isIncluded(target: any, elements: (HTMLElement | null | undefined)[]) {
  return elements.some(el => el && (el === target || el.contains(target as Node)));
}

function resolveOptions(binding: any, vnode: VNode): Required<Options> {
  const value = binding.value;
  let o: Options;
  if (typeof value === 'function') o = { handler: value };
  else o = value || { handler: () => {} };
  return { ...defaults, ...o, vm: vnode.context } as Required<Options>;
}

function onPointerDown(e: Event, ctx: Ctx) {
  ctx._lastMousedown = e;
}

function onPointerUp(e: Event, ctx: Ctx) {
  const { el, options } = ctx;
  if (!options.isActive || !isBrowser()) return;

  const path = getPath(e as any);
  const downPath = ctx._lastMousedown ? getPath(ctx._lastMousedown as any) : path;

  const includes = [el, ...options.include()];
  const clickedEl = (e as any).target as HTMLElement;

  if (isIncluded(clickedEl, includes)) return;

  const downInside = isIncluded(downPath[0], includes);
  const upInside = isIncluded(path[0], includes);
  if (downInside && upInside) return;

  if (typeof options.closeConditional === 'function' && !options.closeConditional(e)) return;

  options.handler(e);
  if (!options.bubble) (e as any).stopPropagation?.();
}

function addListeners(ctx: Ctx) {
  if (!isBrowser() || ctx._listening) return;
  const opts: AddEventListenerOptions = { passive: true, capture: true };
  document.addEventListener('mousedown', ctx._onDown, opts);
  document.addEventListener('mouseup', ctx._onUp, opts);
  document.addEventListener('touchstart', ctx._onDown, opts);
  document.addEventListener('touchend', ctx._onUp, opts);
  ctx._listening = true;
}

function removeListeners(ctx: Ctx) {
  if (!isBrowser() || !ctx._listening) return;
  const opts: AddEventListenerOptions = { capture: true };
  document.removeEventListener('mousedown', ctx._onDown, opts as any);
  document.removeEventListener('mouseup', ctx._onUp, opts as any);
  document.removeEventListener('touchstart', ctx._onDown, opts as any);
  document.removeEventListener('touchend', ctx._onUp, opts as any);
  ctx._listening = false;
}

export const ClickOutside = Vue.directive('click-outside', {
  inserted(el: HTMLElement, binding, vnode: VNode) {
    const options = resolveOptions(binding, vnode);

    const ctx: Ctx = {
      el,
      options,
      _listening: false,
      _lastMousedown: null,
      _onDown: (e: Event) => onPointerDown(e, ctx),
      _onUp:   (e: Event) => onPointerUp(e, ctx),
    };

    (el as any).__vClickOutside__ = ctx;
    if (options.isActive) addListeners(ctx);
  },

  componentUpdated(el: HTMLElement, binding, vnode: VNode) {
    const ctx: Ctx = (el as any).__vClickOutside__;
    if (!ctx) return;
    ctx.options = resolveOptions(binding, vnode);
    if (ctx.options.isActive) addListeners(ctx);
    else removeListeners(ctx);
  },

  unbind(el: HTMLElement) {
    const ctx: Ctx = (el as any).__vClickOutside__;
    if (!ctx) return;
    removeListeners(ctx);
    delete (el as any).__vClickOutside__;
  },
});
